
'use client';

import { useState, useEffect } from 'react';
import { GradientText } from '@/components/ui/gradient-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Image from 'next/image';
import { Linkedin, Twitter, Instagram, Loader2 } from 'lucide-react';
import Link from 'next/link';

interface Socials {
  linkedin?: string;
  twitter?: string;
  instagram?: string;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image_url: string;
  description: string;
  socials: Socials;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

async function fetchTeamMembers(): Promise<TeamMember[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/team`, { next: { revalidate: 600 } });
     if (!response.ok) {
        throw new Error('Failed to fetch team members');
    }
    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error("Error fetching team members:", error);
    return [];
  }
}

export default function TeamPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadTeam() {
      setIsLoading(true);
      const members = await fetchTeamMembers();
      setTeamMembers(members);
      setIsLoading(false);
    }
    loadTeam();
  }, []);

  return (
    <div className="container mx-auto max-w-5xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Meet Our <GradientText>Team</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          The brilliant minds behind our innovative solutions.
        </p>
      </div>

       {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : teamMembers.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {teamMembers.map((member) => (
            <Card key={member.id} className="group relative overflow-hidden text-center transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-2">
              <CardHeader className="p-0 flex items-center justify-center">
                 <Image
                  src={member.image_url || 'https://placehold.co/400x400.png'}
                  alt={`Portrait of ${member.name}`}
                  width={400}
                  height={400}
                  className="rounded-t-lg transition-transform duration-300 group-hover:scale-105"
                  data-ai-hint="person portrait"
                />
              </CardHeader>
              <div className="absolute inset-0 bg-black bg-opacity-0 transition-all duration-300 group-hover:bg-opacity-70 flex flex-col justify-center items-center p-6">
                  <div className="opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <p className="text-white text-sm mb-4">{member.description}</p>
                      <div className="flex justify-center gap-4">
                          {member.socials?.linkedin && (
                            <Link href={member.socials.linkedin} target="_blank" rel="noopener noreferrer">
                                <Linkedin className="h-6 w-6 text-white hover:text-primary transition-colors" />
                            </Link>
                          )}
                           {member.socials?.twitter && (
                            <Link href={member.socials.twitter} target="_blank" rel="noopener noreferrer">
                                <Twitter className="h-6 w-6 text-white hover:text-primary transition-colors" />
                            </Link>
                           )}
                           {member.socials?.instagram && (
                            <Link href={member.socials.instagram} target="_blank" rel="noopener noreferrer">
                                <Instagram className="h-6 w-6 text-white hover:text-primary transition-colors" />
                            </Link>
                           )}
                      </div>
                  </div>
              </div>
              <CardContent className="p-6 absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent">
                <CardTitle className="text-xl font-headline text-white">{member.name}</CardTitle>
                <p className="text-primary">{member.role}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted-foreground py-16">
          <p className="text-lg">Our team is currently assembling. Check back soon!</p>
        </div>
      )}
    </div>
  );
}
